/**
 * Normalise un timestamp pouvant être :
 *  - Unix SECONDES (Outscraper.review_timestamp, ex 1709212800)
 *  - Unix MILLISECONDES (Date.now(), ex 1709212800000)
 *  - String ISO (ex "2026-04-25T...")
 *  - Date object
 *
 * Seuil 1e11 : tout ce qui est en dessous est interprété comme secondes
 * (cohérent avec backend/routes/dashboard.js helper équivalent).
 *
 * @returns {number} timestamp en millisecondes (ou NaN si parsing impossible)
 */
export function normalizeTimestamp(value) {
  if (value == null) return NaN;
  if (typeof value === 'number') {
    return value < 1e11 ? value * 1000 : value;
  }
  if (typeof value === 'string') {
    // Si c'est une string numérique, on la repasse en number
    const asNum = Number(value);
    if (!Number.isNaN(asNum) && /^\d+$/.test(value)) {
      return asNum < 1e11 ? asNum * 1000 : asNum;
    }
    return new Date(value).getTime();
  }
  if (value instanceof Date) return value.getTime();
  return NaN;
}

/**
 * Date au format relatif français : "à l'instant", "il y a 2j", "il y a 1 mois"...
 */
export function formatRelativeDate(value) {
  const ms = normalizeTimestamp(value);
  if (Number.isNaN(ms)) return '';
  const diffSec = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (diffSec < 60) return "à l'instant";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `il y a ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `il y a ${diffH} h`;
  const diffDays = Math.floor(diffH / 24);
  if (diffDays < 7) return `il y a ${diffDays} j`;
  const diffWeeks = Math.floor(diffDays / 7);
  if (diffDays < 30) return `il y a ${diffWeeks} sem`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffDays < 365) return `il y a ${diffMonths} mois`;
  const diffYears = Math.floor(diffDays / 365);
  return `il y a ${diffYears} an${diffYears > 1 ? 's' : ''}`;
}

/**
 * Date au format absolu français abrégé : "14 fév 2026"
 */
export function formatAbsoluteDate(value) {
  const ms = normalizeTimestamp(value);
  if (Number.isNaN(ms)) return '';
  return new Date(ms).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Hybride : relatif si <30 jours, absolu sinon.
 * C'est le format utilisé dans les ReviewCard.
 */
export function formatHybridDate(value) {
  const ms = normalizeTimestamp(value);
  if (Number.isNaN(ms)) return '';
  const diffDays = (Date.now() - ms) / 86400000;
  return diffDays < 30 ? formatRelativeDate(value) : formatAbsoluteDate(value);
}

/**
 * "il y a 5 min" / "il y a 2 h" pour le label "Mis à jour il y a X"
 * (alias de formatRelativeDate pour clarté d'intention)
 */
export const formatTimeAgo = formatRelativeDate;
